import './PromptsTable.scss';
import SimpleBar from 'simplebar-react';
import { DataTable } from '../common/data-table/DataTable';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Column } from 'primereact/column';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PromptsData } from '../../../pages/prompt/models/PromptsData';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { ADD, PROJECTS, QUESTIONS } from '../../routes/path';
import { Button } from 'primereact/button';
import { SplitButton } from 'primereact/splitbutton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import ImportPromptsDialog from '../import-prompts-dialog/ImportPromptsDialog';
import { Tooltip } from 'primereact/tooltip';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { useAccount } from '../../contexts/account/AccountContexts';
import {
  useEffectiveAddons,
  useEffectiveFeatures,
} from '../../contexts/effective-features/EffectiveFeaturesContext';
import {
  bulkPauseMonitors,
  bulkRemoveMonitors,
  bulkRunMonitors,
  pauseMonitor,
  runMonitor,
} from '../../services/monitor/prompt/PromptService';
import { PeriodType } from '../../types/prompt/PeriodType';
import { ChatType } from '../../types/monitor/ChatType';
import { getPeriodUrl, prepareAndGetChatType } from '../../helpers/StatsHelpers';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import LimitTooltipComponent from '../common/limit-tooltip/LimitTooltipComponent';
import Title from '../title/Title';
import CustomTooltip from '../common/tooltip/CustomTooltip';
import NoResults from '../common/no-results/NoResults';
import MoreBtn from '../common/more-btn/MoreBtn';
import useCanManage from '../../hooks/account/UseCanManage';
import usePromptFitDialog from '../../hooks/prompt/UsePromptFitDialog';
import { useGetProject } from '../../api/hooks/project/useGetProject';
import { allocationRoom, isParkedByHub, promptsInUse } from '../../helpers/prompt/promptFit';
import usePermission from '../../hooks/global/UsePermission';
import { MonitorStatus } from '../../types/prompt/MonitorStatusType';
import { Nullable } from 'primereact/ts-helpers';
import useRemovePrompt from '../../hooks/prompt/UseRemovePrompt';
import { createCustomColumn } from '../../helpers/ColumnHelper';
import { isModelKeySelected } from '../../helpers/MonitorTypeHelpers';
import { canShowModel, downloadBlobAsFile } from '../../utils/utils';
import { Skeleton } from 'primereact/skeleton';
import { useMutation } from '@tanstack/react-query';
import { Checkbox } from 'primereact/checkbox';
import { Menu } from 'primereact/menu';
import useIsMobile from '../../hooks/global/UseIsMobile';
import MobilePageHeader from '../common/mobile-menu/MobilePageHeader';
import MobileHeaderSearch from '../common/mobile-menu/MobileHeaderSearch';

export interface PromptGroupItem {
  id: number;
  name: string;
  color: string | null;
}

interface PromptsTableProp {
  tableTitle: string;
  /** Dopisek do tytułu (np. „for <marka>”) — na desktopie w tej samej linii, na telefonie pod spodem. */
  tableTitleSubject?: string;
  data: PromptsData[] | null;
  range: Nullable<(Date | null)[]>;
  periodType: PeriodType | null;
  chatType: ChatType;
  projectId?: number | undefined;
  additionalClass?: string;
  refreshAfterDelete: () => void;
  loader?: () => void;
  isLoading?: boolean;
  modelsWithoutData?: string[];
  // Real first/last data date per model (model key → {from,to} ISO strings), within the calendar
  // range. Used for the archival-model "?" tooltip instead of the raw calendar range.
  modelDataRanges?: Record<string, { from: string; to: string }>;
  selectedChatTypes?: ChatType[];
  groups?: PromptGroupItem[];
  hasPromptGroupsAddon?: boolean;
  onAssignToGroup?: (promptId: number, groupId: number | null) => void;
  onBulkAssignToGroup?: (promptIds: number[], groupId: number | null) => void;
  renderGroupHeader?: (
    group: PromptGroupItem,
    promptCount: number,
    isCollapsed: boolean,
    onToggleCollapse: () => void
  ) => React.ReactNode;
  onCreateGroup?: () => void;
  showStats?: boolean;
  showKeyword?: boolean;
  // V2 / NEW_STATS only: render a per-question Visibility % column (selected brand's mention
  // rate for the question). Off by default so V1 and the prompts management page are unchanged.
  showVisibility?: boolean;
  // Render "ChatGPT Ads %" / "Shopping Cards %" columns (share of the period's answers containing
  // them). Requires rows enriched with `promptFeatures` (usePromptsData); off by default so
  // consumers without that data (e.g. the prompts management page) don't render dash-only columns.
  showFeatures?: boolean;
  headerExtra?: React.ReactNode;
  onReorderGroups?: (orderedIds: number[]) => void;
  groupDisplayMode?: 'sections' | 'accordion';
  stickyHeader?: boolean;
  // Always render a column for every model on the project owner's account, even when no prompt has
  // data for it yet (NEW_STATS otherwise shows only models with data). Models with data but no longer
  // on the account stay visible too. Used on the V2 preview so the account's models never disappear.
  alwaysShowAccountModels?: boolean;
  // When set, the table body scrolls within this height and the column header (thead) stays pinned
  // at the top of that scroll (e.g. '70vh'). Needed because the horizontally-scrollable wrapper
  // prevents a page-level sticky thead — see PromptsTable.scss.
  scrollHeight?: string;
}

// Wraps children in our SimpleBar custom scrollbar when `enabled`, otherwise renders them untouched.
// Gives the prompts table a cross-browser-consistent scrollbar while keeping PrimeReact's frozen
// action column: the PrimeReact scroll wrapper is forced to overflow:visible in SCSS so SimpleBar owns
// the scroll, and both the sticky thead and the frozen column resolve against SimpleBar's scrollport.
const SimpleBarScroll: React.FC<{ enabled: boolean; maxHeight?: string; children: React.ReactNode }> = ({
  enabled,
  maxHeight,
  children,
}) =>
  enabled ? (
    <SimpleBar style={{ maxHeight }} autoHide={false}>
      {children}
    </SimpleBar>
  ) : (
    <>{children}</>
  );

// Per-model fields (position + share of voice) that signal a model actually returned data for a
// prompt. A model column is shown when ANY prompt has a value here — i.e. we display every model we
// have data for; the account limit lives on the filter only.
const MODEL_KEY_DATA_FIELDS: Record<string, (keyof PromptsData)[]> = {
  GPT: ['averageGPTPosition', 'averageGPTShareOfVoice'],
  CLAUDE: ['averageClaudePosition', 'averageClaudeShareOfVoice'],
  GEMINI: ['averageGeminiPosition', 'averageGeminiShareOfVoice'],
  DEEP_SEEK: ['averageDeepSeekPosition', 'averageDeepSeekShareOfVoice'],
  PERPLEXITY: ['averagePerplexityPosition', 'averagePerplexityShareOfVoice'],
  COPILOT: ['averageCopilotPosition', 'averageCopilotShareOfVoice'],
  GROK: ['averageGrokPosition', 'averageGrokShareOfVoice'],
  AI_OVERVIEW_NEW: ['averageAiOverviewPosition', 'averageAiOverviewShareOfVoice'],
  AI_MODE: ['averageAiModePosition', 'averageAiModeShareOfVoice'],
};

const hasModelValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '-' && value !== 0 && value !== '0' && value !== '';

const PromptsTable: React.FC<PromptsTableProp> = ({
  tableTitle,
  tableTitleSubject,
  data,
  range,
  periodType,
  chatType,
  projectId,
  additionalClass,
  refreshAfterDelete,
  loader,
  isLoading,
  modelsWithoutData,
  modelDataRanges,
  selectedChatTypes,
  groups,
  hasPromptGroupsAddon,
  onAssignToGroup,
  onBulkAssignToGroup,
  renderGroupHeader,
  onCreateGroup,
  showStats = true,
  showKeyword = true,
  showVisibility = false,
  showFeatures = false,
  headerExtra,
  onReorderGroups,
  groupDisplayMode = 'sections',
  stickyHeader = false,
  alwaysShowAccountModels = false,
  scrollHeight,
}) => {
  // Telefon: wiersze jako karty (`table-wrapper--stack`), bez wewnętrznego przewijania tabeli.
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  // Owner's models/addons when a superadmin views a client's project; own config elsewhere.
  const effectiveFeatures = useEffectiveFeatures();
  const effectiveAddons = useEffectiveAddons();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canManage } = useCanManage();
  // The project's allocation (Agency Hub accounts): the add and resume controls close when it is full,
  // and a prompt the hub's project pause parked only comes back with the project's Start. Usage is
  // counted from the rows (the list is the whole project), the allocation from the project read.
  const { project: allocationProject } = useGetProject(projectId ?? null);
  const promptsUsed = useMemo(() => promptsInUse(data ?? []), [data]);
  const allocationLeft = allocationRoom(allocationProject, promptsUsed);
  const allocationFull = allocationLeft !== null && allocationLeft <= 0;
  // A project without an allocation takes from the pool left next to every other reservation, so a
  // pool the reservations filled closes the add controls too (plain usage without the hub).
  const poolFull =
    allocationLeft === null &&
    !!accountResponse &&
    accountResponse.allocatedMonitorCount >= accountResponse.maxMonitorCount;
  const canAdd = canManage && !allocationFull && !poolFull;
  const { showPromptFitError, showAllocationLimit, showProjectPaused } = usePromptFitDialog({
    onRefresh: refreshAfterDelete,
  });
  const projectsAccess = usePermission('PROJECTS');
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [importDialogVisible, setImportDialogVisible] = useState(false);
  const splitButtonRef = useRef<SplitButton>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  const [ungroupedCollapsed, setUngroupedCollapsed] = useState(false);
  const bulkMoveMenuRef = useRef<Menu>(null);
  const moveMenuRefs = useRef<Map<number, Menu>>(new Map());
  const [openMoveMenuId, setOpenMoveMenuId] = useState<number | null>(null);
  const [draggedGroupId, setDraggedGroupId] = useState<number | null>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<number | null>(null);
  // Single gate for the Ads/Shopping columns (table + CSV export) — features are stats columns,
  // hidden behind the NEW_PROMPTS_STATS addon.
  const showFeaturesColumns =
    showStats && showFeatures && !!accountResponse?.addons?.includes('NEW_PROMPTS_STATS');
  const [dragOverGroupIdForPrompt, setDragOverGroupIdForPrompt] = useState<number | 'untagged' | null>(
    null
  );
  const [draggedPromptId, setDraggedPromptId] = useState<number | null>(null);
  const [expandedAccordionGroups, setExpandedAccordionGroups] = useState<Set<number>>(new Set());
  // Accordion pagination is by "unit" (group-summary rows + ungrouped prompt rows), NOT by every
  // rendered row — so a group's expanded children are always shown in full under it on the same page
  // (never split onto the next page). Driven manually via DataTable `lazy` mode.
  const [accordionFirst, setAccordionFirst] = useState(0);
  const [accordionRows, setAccordionRows] = useState(25);
  // Accordion sorting is applied by hand (DataTable runs in `lazy` mode, so it never reorders the
  // interleaved group/child rows itself). Groups keep their configured order — sorting only reorders
  // the ungrouped prompts and the children inside each group.
  const [accordionSortField, setAccordionSortField] = useState<string | null>('averagePosition');
  const [accordionSortOrder, setAccordionSortOrder] = useState<1 | -1 | null>(1);
  // Groups currently playing the close animation: kept in expandedAccordionGroups while the
  // collapse animation plays, then removed by a timer.
  const [collapsingGroups, setCollapsingGroups] = useState<Set<number>>(new Set());
  const collapseTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  // Tracks which group was most recently expanded so accordion-row--enter animation plays once.
  const [recentlyExpandedGroup, setRecentlyExpandedGroup] = useState<number | null>(null);
  const expandAnimTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const groupedContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openMoveMenuId) return;
    const handleScroll = () => {
      const ref = moveMenuRefs.current.get(openMoveMenuId);
      ref?.hide({} as React.SyntheticEvent);
      setOpenMoveMenuId(null);
    };
    const layoutElement = document.querySelector('.layout__right');
    if (layoutElement) {
      layoutElement.addEventListener('scroll', handleScroll);
      return () => layoutElement.removeEventListener('scroll', handleScroll);
    }
  }, [openMoveMenuId]);

  useEffect(() => {
    if (!groupedContentRef.current || !onAssignToGroup) return;
    const rows = groupedContentRef.current.querySelectorAll('tbody tr');
    rows.forEach((tr) => {
      tr.setAttribute('draggable', 'true');
      tr.querySelectorAll('td').forEach((td) => td.setAttribute('draggable', 'true'));
    });
  });

  const showGroups = !!(hasPromptGroupsAddon && Array.isArray(groups) && groups.length > 0);

  const promptsByGroup = useMemo<Map<number | null, PromptsData[]>>(() => {
    if (!showGroups || !data) return new Map();
    const validGroupIds = new Set((groups ?? []).map((g) => g.id));
    const map = new Map<number | null, PromptsData[]>();
    data.forEach((prompt) => {
      const groupId = prompt.promptGroupId;
      const key = groupId != null && validGroupIds.has(groupId) ? groupId : null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(prompt);
    });
    return map;
  }, [showGroups, data, groups]);

  const isAccordionMode = groupDisplayMode === 'accordion' && showGroups;

  // Accordion search: matches prompt text only (group names are not searched).
  const accordionSearch = globalFilterValue.trim().toLowerCase();
  const matchesAccordionSearch = useCallback(
    (p: PromptsData) => !accordionSearch || (p.prompt ?? '').toLowerCase().includes(accordionSearch),
    [accordionSearch]
  );

  const accordionData = useMemo(() => {
    if (!isAccordionMode || !data || !groups) return null;

    // Numeric average (raw) — used as a hidden `sortField` so columns sort numerically, not lexically.
    const avgFieldNum = (prompts: PromptsData[], field: keyof PromptsData): number | undefined => {
      const vals = prompts.map((p) => parseFloat(p[field] as string)).filter((v) => !isNaN(v));
      if (vals.length === 0) return undefined;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    };

    const avgField = (prompts: PromptsData[], field: keyof PromptsData): string | undefined => {
      const avg = avgFieldNum(prompts, field);
      if (avg === undefined) return undefined;
      return avg % 1 === 0 ? String(avg) : avg.toFixed(1);
    };

    // Stats are computed from the WHOLE group (`childPrompts`); only the displayed children are filtered.
    const makeGroupRow = (
      id: number,
      name: string,
      color: string | null,
      childPrompts: PromptsData[],
      visibleChildren: PromptsData[]
    ) => ({
      _isGroupRow: true as const,
      _groupId: id,
      _groupName: name,
      _groupColor: color,
      _childCount: childPrompts.length,
      _childPrompts: visibleChildren,
      id: -(id),
      prompt: name,
      keyword: '',
      searchKeyword: '',
      status: 'ACTIVE' as const,
      projectId: null,
      demoVersion: null,
      isRequiredAttention: false,
      defaultPeriod: 'DAILY' as const,
      hasAiOverview: false,
      averagePosition: avgField(childPrompts, 'averagePosition'),
      averagePositionAll: avgField(childPrompts, 'averagePositionAll'),
      averageGPTPosition: avgField(childPrompts, 'averageGPTPosition'),
      averageClaudePosition: avgField(childPrompts, 'averageClaudePosition'),
      averageGeminiPosition: avgField(childPrompts, 'averageGeminiPosition'),
      averageDeepSeekPosition: avgField(childPrompts, 'averageDeepSeekPosition'),
      averagePerplexityPosition: avgField(childPrompts, 'averagePerplexityPosition'),
      averageCopilotPosition: avgField(childPrompts, 'averageCopilotPosition'),
      averageGrokPosition: avgField(childPrompts, 'averageGrokPosition'),
      averageAiOverviewPosition: avgField(childPrompts, 'averageAiOverviewPosition'),
      averageAiModePosition: avgField(childPrompts, 'averageAiModePosition'),
      searchVolume: avgField(childPrompts, 'searchVolume'),
      visibility: avgField(childPrompts, 'visibility'),
      visibilityCurrent: avgField(childPrompts, 'visibilityCurrent'),
      chatgptAdsPercent: avgField(childPrompts, 'chatgptAdsPercent'),
      shoppingCardsPercent: avgField(childPrompts, 'shoppingCardsPercent'),
      // shareOfVoice on child prompts is a "%"-suffixed string (e.g. "12.3%"); avgField's parseFloat
      // ignores the suffix, so the group average is the plain number (rendered with % in the column).
      shareOfVoice: avgField(childPrompts, 'shareOfVoice'),
      // Numeric twins for sorting the group rows (display fields above are formatted strings).
      averagePositionNum: avgFieldNum(childPrompts, 'averagePosition'),
      visibilityNum: avgFieldNum(childPrompts, 'visibility'),
    });

    const result: any[] = [];
    groups.forEach((group) => {
      const childPrompts = promptsByGroup.get(group.id) || [];
      if (childPrompts.length === 0) return;
      const visibleChildren = childPrompts.filter(matchesAccordionSearch);
      if (visibleChildren.length === 0) return;
      const row = makeGroupRow(group.id, group.name, group.color, childPrompts, visibleChildren) as any;
      // Alternate the group-row background by group index (every 2nd non-empty group), so adjacent
      // groups are easier to tell apart. Index-based, NOT table-position-based.
      row._groupEven = result.length % 2 === 1;
      result.push(row);
    });

    return result;
  }, [isAccordionMode, data, groups, promptsByGroup, matchesAccordionSearch]);

  useEffect(() => {
    if (!isAccordionMode) return;
    setAccordionFirst(0);
  }, [accordionSearch, isAccordionMode]);

  useEffect(() => {
    if (!isAccordionMode || !accordionData || !globalFilterValue) return;
    setExpandedAccordionGroups(new Set(accordionData.map((row: any) => row._groupId)));
  }, [globalFilterValue, isAccordionMode, accordionData]);

  // Build the interleaved row array for the single-table accordion:
  // Paginatable UNITS: each ungrouped prompt row (first), then one per group-summary row. Expanded
  // child prompts are deliberately NOT units — they ride along with their group (see accordionPageRows),
  // so a group's children never get split across a page break.
  // Sorts a flat list of prompts by the active accordion sort column. Null when no column is sorted.
  // Values are display strings ("12.3%", "1.5"), so compare numerically and fall back to a locale
  // compare for text columns; rows with genuinely no value ("-", empty) always sink to the bottom,
  // in both directions.
  const sortAccordionPrompts = useMemo(() => {
    if (!accordionSortField || !accordionSortOrder) return null;
    const field = accordionSortField as keyof PromptsData;
    const order = accordionSortOrder;
    // "<100" is searchVolume's placeholder for any volume at or below 100 (see UseProjectData) —
    // parse the threshold and nudge it below, otherwise parseFloat returns NaN and those prompts
    // sink to the bottom despite being the SMALLEST values.
    const toNumber = (value: unknown): number => {
      const raw = String(value ?? '').trim();
      const isCapped = raw.startsWith('<');
      const parsed = parseFloat(isCapped ? raw.slice(1) : raw);
      if (isNaN(parsed)) return NaN;
      return isCapped ? parsed - 0.5 : parsed;
    };
    return (prompts: PromptsData[]): PromptsData[] =>
      [...prompts].sort((a, b) => {
        const aValue = a[field];
        const bValue = b[field];
        const aNum = toNumber(aValue);
        const bNum = toNumber(bValue);
        if (isNaN(aNum) && isNaN(bNum)) {
          return String(aValue ?? '').localeCompare(String(bValue ?? ''), undefined, { numeric: true }) * order;
        }
        if (isNaN(aNum)) return 1;
        if (isNaN(bNum)) return -1;
        return (aNum - bNum) * order;
      });
  }, [accordionSortField, accordionSortOrder]);

  const accordionUnits = useMemo<any[]>(() => {
    if (!isAccordionMode || !accordionData) return [];
    const units: any[] = [];
    // Ungrouped prompts go FIRST (top of the table), potem wiersze-nagłówki grup. Sortowanie dotyczy
    // OBU sekcji z osobna: kliknięcie w „Visibility" albo „Average Position" przestawia też same
    // grupy, a nie tylko prompty w środku. Bez tego przy kilkunastu grupach na ekranie nie ruszało
    // się nic widocznego i wyglądało to na zepsuty przycisk (zgłoszenie użytkownika).
    //
    // Wiersz grupy niesie te same pola co prompt — `averagePosition`, `visibility` itd. są średnimi
    // z jej promptów (patrz `accordionData`) — więc ten sam komparator działa na obu bez wyjątków.
    // Kolejność konfiguracyjna grup wraca, gdy sortowanie jest wyłączone (`removableSort`).
    const ungrouped: PromptsData[] = (promptsByGroup.get(null) || []).filter(matchesAccordionSearch);
    (sortAccordionPrompts ? sortAccordionPrompts(ungrouped) : ungrouped).forEach((p) => units.push(p));
    const groupRows: PromptsData[] = accordionData as unknown as PromptsData[];
    (sortAccordionPrompts ? sortAccordionPrompts(groupRows) : groupRows).forEach((groupRow: any) =>
      units.push(groupRow)
    );
    return units;
  }, [isAccordionMode, accordionData, promptsByGroup, sortAccordionPrompts, matchesAccordionSearch]);

  // The rows actually rendered for the current page: the current slice of UNITS with expanded group
  // children interleaved directly after their group-summary row. (No synthetic repeated column header
  // above the ungrouped prompts — the real thead is now sticky, so the duplicate would be redundant.)
  const accordionPageRows = useMemo<any[]>(() => {
    const slice = accordionUnits.slice(accordionFirst, accordionFirst + accordionRows);
    const rows: any[] = [];
    let ungroupedIdx = 0;
    slice.forEach((unit: any) => {
      if (unit._isGroupRow) {
        rows.push(unit);
        if (expandedAccordionGroups.has(unit._groupId)) {
          // Tag each child with its parity WITHIN the group so striping restarts per group and is
          // unaffected by other groups expanding/collapsing (the global nth-child zebra is disabled
          // for the accordion table — see SCSS).
          const children: PromptsData[] = unit._childPrompts || [];
          // `_groupLast` zamyka kartę grupy na telefonie (dolna ramka pod ostatnim pytaniem).
          (sortAccordionPrompts ? sortAccordionPrompts(children) : children).forEach(
            (p: PromptsData, ci: number) =>
              rows.push({ ...p, _zebraEven: ci % 2 === 1, _groupLast: ci === children.length - 1 })
          );
        }
      } else {
        rows.push({ ...unit, _zebraEven: ungroupedIdx % 2 === 1 });
        ungroupedIdx += 1;
      }
    });
    return rows;
  }, [accordionUnits, accordionFirst, accordionRows, expandedAccordionGroups, sortAccordionPrompts]);

  useEffect(() => {
    const handleScroll = () => {
      try { splitButtonRef.current?.hide(); } catch {}
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  // Clear any pending collapse timers on unmount.
  useEffect(() => {
    const timers = collapseTimers.current;
    const expandTimer = expandAnimTimer;
    return () => {
      timers.forEach((tid) => clearTimeout(tid));
      timers.clear();
      if (expandTimer.current !== null) {
        clearTimeout(expandTimer.current);
        expandTimer.current = null;
      }
    };
  }, []);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState<PromptsData[]>([]);
  const hasExplicitModelFilter = searchParams.get('models') !== null;

  const modelsWithData = useMemo(() => {
    const set = new Set<string>();
    const rows = data ?? [];
    Object.keys(MODEL_KEY_DATA_FIELDS).forEach((key) => {
      if (rows.some((row) => MODEL_KEY_DATA_FIELDS[key].some((field) => hasModelValue(row[field])))) {
        set.add(key);
      }
    });
    return set;
  }, [data]);

  const hasNewStats = effectiveAddons.includes('NEW_STATS');

  // A model column is "archival" when it has data but the model is no longer on the account (only
  // possible with NEW_STATS, which surfaces historical models). Drives the "?" header + greyed cells.
  const isHistoricalModel = (modelKey: string): boolean =>
    hasNewStats && !canShowModel(modelKey, effectiveFeatures);

  // Group aggregate-row cell for a per-model column — mirrors the archival grey applied to detail cells.
  const groupModelCell = (value: unknown, modelKey: string): React.ReactNode => (
    <span className={isHistoricalModel(modelKey) ? 'prompts-table__archival-cell' : undefined}>
      {(value as React.ReactNode) || '-'}
    </span>
  );

  const isModelColumnHidden = (modelKey: string): boolean => {
    if (chatType !== 'ALL') return true;
    // NEW_STATS accounts: show a column when ANY prompt has data for that model. Without the addon,
    // keep the legacy gating that shows only the account's models.
    // Pending project (no model has data yet): fall back to the account's configured models so the
    // columns don't all vanish before the first data arrives — they match the owner's account.
    const onAccount = canShowModel(modelKey, effectiveFeatures);
    // V2 preview: always keep the account's models visible (union with models that have data), so a
    // configured model never disappears just because it has no data in the current range yet.
    if (alwaysShowAccountModels) {
      if (!onAccount && !modelsWithData.has(modelKey)) {
        return true;
      }
    } else {
      const useAccountModels = !hasNewStats || modelsWithData.size === 0;
      if (useAccountModels ? !onAccount : !modelsWithData.has(modelKey)) {
        return true;
      }
    }
    if (hasExplicitModelFilter && selectedChatTypes && selectedChatTypes.length > 0) {
      return !isModelKeySelected(modelKey, selectedChatTypes);
    }
    return false;
  };

  // Tooltip for an archival model — the REAL first/last data date of that model (within the calendar
  // range), falling back to a generic message when the range isn't available.
  const historicalModelTooltip = (modelKey: string): string => {
    const dataRange = modelDataRanges?.[modelKey];
    if (dataRange?.from && dataRange?.to) {
      return t('historicalModelDisabledRange', {
        from: format(new Date(dataRange.from), 'd MMM yyyy'),
        to: format(new Date(dataRange.to), 'd MMM yyyy'),
      });
    }
    return t('historicalModelDisabled');
  };

  // Per-model column header. When a model has data but is NOT currently on the account, append a "?"
  // with the historical/disabled tooltip; otherwise just the model name.
  const renderModelHeader = (modelKey: string, nameKey: string): React.ReactNode => {
    const isHistorical = isHistoricalModel(modelKey);
    // Telefon: nagłówek jest też etykietą w każdej karcie — bez ikonki i tooltipu (kopia w każdym
    // wierszu dawałaby wiele dymków naraz).
    if (!isHistorical || isMobile) {
      return t(nameKey);
    }
    // Unique id per render so each "?" icon (the header renders once per flat table and once per group
    // table) gets its own Tooltip — a single class-targeted Tooltip loses its binding when the DataTable
    // re-renders the headers.
    const iconId = `hist-model-${modelKey}-${uuidv4()}`;
    return (
      <span className="prompts-table__model-header">
        {t(nameKey)}
        <span className="ico--info ico--xxs" id={iconId} />
        <Tooltip
          target={`#${iconId}`}
          content={historicalModelTooltip(modelKey)}
          className="tooltip--top"
          position="top"
          showDelay={5}
          style={{ width: '240px' }}
        />
      </span>
    );
  };

  // Columns to put in the exported CSV — mirrors the columns currently visible in the flat table
  // (same flags/model gating), so the file matches what the user sees.
  const getExportColumns = (): { header: string; value: (row: PromptsData) => unknown }[] => {
    const models: [string, string, keyof PromptsData][] = [
      ['GPT', 'gptName', 'averageGPTPosition'],
      ['CLAUDE', 'claudeName', 'averageClaudePosition'],
      ['GEMINI', 'geminiName', 'averageGeminiPosition'],
      ['DEEP_SEEK', 'deepSeekName', 'averageDeepSeekPosition'],
      ['PERPLEXITY', 'perplexityName', 'averagePerplexityPosition'],
      ['COPILOT', 'copilotSearchName', 'averageCopilotPosition'],
      ['GROK', 'grokSearchName', 'averageGrokPosition'],
      ['AI_OVERVIEW_NEW', 'aiOverviewName', 'averageAiOverviewPosition'],
      ['AI_MODE', 'aiModeName', 'averageAiModePosition'],
    ];
    const cols: { header: string; value: (row: PromptsData) => unknown }[] = [
      { header: t('status'), value: (r) => r.status },
      { header: t('prompt'), value: (r) => r.prompt },
    ];
    if (showKeyword) cols.push({ header: t('keyword'), value: (r) => r.keyword });
    if (showStats && chatType === 'ALL')
      cols.push({ header: t('searchVolume'), value: (r) => r.searchVolume });
    if (showStats) cols.push({ header: t('averagePosition'), value: (r) => r.averagePosition });
    if (showStats && showVisibility)
      cols.push({ header: t('visibility'), value: (r) => r.visibility });
    if (showFeaturesColumns) {
      cols.push({ header: `${t('chatgptAds')} %`, value: (r) => r.chatgptAdsPercent });
      cols.push({ header: `${t('shoppingCards')} %`, value: (r) => r.shoppingCardsPercent });
    }
    if (showStats)
      models.forEach(([modelKey, nameKey, field]) => {
        if (!isModelColumnHidden(modelKey)) cols.push({ header: t(nameKey), value: (r) => r[field] });
      });
    if (showStats && chatType !== 'ALL')
      cols.push({ header: t('averageShareOfVoice'), value: (r) => r.shareOfVoice });
    return cols;
  };

  const canExportData = !!accountResponse?.addons?.includes('EXPORT_DATA');

  const handleExportSelectedCsv = () => {
    if (!canExportData || selectedPrompts.length === 0) return;
    const columns = getExportColumns();
    const escape = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      columns.map((c) => escape(c.header)).join(','),
      ...selectedPrompts.map((row) => columns.map((c) => escape(c.value(row))).join(',')),
    ];
    // Prepend BOM so Excel reads UTF-8 correctly.
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const fullTitle = [tableTitle, tableTitleSubject].filter(Boolean).join(' ');
    const safeName = (fullTitle || 'prompts').replace(/[\\/:*?"<>|]+/g, '_').trim();
    downloadBlobAsFile(blob, `${safeName}.csv`);
  };

  const { confirmRemove } = useRemovePrompt({
    refreshAfterDelete,
    loader,
  });
  const bulkPauseMutation = useMutation({
    mutationFn: () =>
      bulkPauseMonitors(
        selectedPrompts.map((p) => p.id!).filter((id): id is number => id !== undefined)
      ),
    onSuccess: () => {
      setSelectedPrompts([]);
      refreshAfterDelete();
    },
    onError: (error: any) => {
      if (error?.response?.data?.error === 'PAUSE_LIMIT_EXCEEDED') {
        confirmDialog({
          message: t('pauseLimitExceeded', {
            currentPaused: error.response.data.currentPaused,
            maxPausedMonitors: error.response.data.maxPausedMonitors,
          }),
          header: t('limitExceeded'),
          acceptLabel: 'OK',
          rejectClassName: 'hide',
        });
      } else {
        console.error('Bulk pause failed');
      }
    },
  });

  const bulkRunMutation = useMutation({
    mutationFn: () =>
      bulkRunMonitors(selectedPrompts.map((p) => p.id!).filter((id): id is number => id !== undefined)),
    onSuccess: () => {
      setSelectedPrompts([]);
      refreshAfterDelete();
    },
    onError: (error: any) => {
      if (showPromptFitError(error)) {
        return;
      }
      if (error?.response?.data?.error === 'LIMIT_EXCEEDED') {
        confirmDialog({
          message: t('bulkLimitExceeded', {
            currentActive: error.response.data.currentActive,
            maxMonitors: error.response.data.maxMonitors,
          }),
          header: t('limitExceeded'),
          acceptLabel: 'OK',
          rejectClassName: 'hide',
        });
      } else {
        console.error('Bulk run failed');
      }
    },
  });

  const bulkRemoveMutation = useMutation({
    mutationFn: () =>
      bulkRemoveMonitors(
        selectedPrompts.map((p) => p.id!).filter((id): id is number => id !== undefined)
      ),
    onSuccess: () => {
      setSelectedPrompts([]);
      refreshAfterDelete();
    },
    onError: () => console.error('Bulk remove failed'),
  });

  const confirmBulkRemove = () => {
    confirmDialog({
      message: t('areYouSureProceed'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => bulkRemoveMutation.mutate(),
    });
  };

  const confirmBulkPause = () => {
    confirmDialog({
      message: t('confirmPausePrompts'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => bulkPauseMutation.mutate(),
    });
  };

  const toggleMonitor = (id: number | undefined, monitorStatus: MonitorStatus) => {
    if (!id) {
      return;
    }

    if (['ACTIVE'].includes(monitorStatus)) {
      confirmDialog({
        message: t('confirmPausePrompts'),
        header: t('areYouSure'),
        defaultFocus: 'accept',
        accept: () => {
          pauseMonitor(id)
            .catch((error: any) => {
              if (error?.response?.data?.error === 'PAUSE_LIMIT_EXCEEDED') {
                confirmDialog({
                  message: t('pauseLimitExceeded', {
                    currentPaused: error.response.data.currentPaused,
                    maxPausedMonitors: error.response.data.maxPausedMonitors,
                  }),
                  header: t('limitExceeded'),
                  acceptLabel: 'OK',
                  rejectClassName: 'hide',
                });
              } else {
                console.error("Error - can't change status");
              }
            })
            .finally(() => refreshAfterDelete());
        },
      });
    } else if (['PAUSED'].includes(monitorStatus)) {
      runMonitor(id)
        .catch((error: any) => {
          if (showPromptFitError(error)) {
            return;
          }
          if (error?.response?.data?.error === 'LIMIT_EXCEEDED') {
            confirmDialog({
              message: t('bulkLimitExceeded', {
                currentActive: error.response.data.currentActive,
                maxMonitors: error.response.data.maxMonitors,
              }),
              defaultFocus: 'accept',
              header: t('limitExceeded'),
              acceptLabel: 'OK',
              rejectClassName: 'hide',
            });
          } else {
            console.error("Error - can't change status");
          }
        })
        .finally(() => refreshAfterDelete());
    }
  };

  const statusBodyTemplate = (rowData: PromptsData) => {
    const rawStatus = rowData.status;
    const status = isOutOfRange(rowData.created) ? 'unavailable' : rawStatus.toLowerCase();
    return (
      <div className={`status status--${status}`} data-prompt-id={rowData.id}>
        <div className="status__ring" />
        <div className="status__dot" />
      </div>
    );
  };

  const keywordBodyTemplate = (rowData: PromptsData) => {
    return rowData.keyword ? (
      <span className={`tag tag--second  ${chatType === 'ALL' ? 'tag--short' : ''}`}>
        {rowData.keyword}
      </span>
    ) : (
      '-'
    );
  };

  const promptBodyTemplate = (rowData: PromptsData) => {
    const defaultPeriodType = periodType === null ? rowData.defaultPeriod : periodType;
    const aiOverviewLink = `${PROJECTS}/${projectId}${QUESTIONS}/${rowData.id}/${getPeriodUrl(
      defaultPeriodType
    )}/${prepareAndGetChatType(accountResponse, 'AI_OVERVIEW_NEW_BROWSER').toLowerCase()}`;

    const isRequiredAttentions = rowData.isRequiredAttention;
    const canShowIndicator = rowData && isRequiredAttentions;
    const suffix = rowData?.demoVersion ? ' (Demo)' : '';
    const prompt = rowData.prompt + suffix;

    const tooltipText = isRequiredAttentions ? 'noBrandsTooltip' : 'mixedWarning';
    const indicator = canShowIndicator && accountResponse?.isSuperAdmin && (
      <>
        <span className="material-symbols-outlined" id={`warming-icon-${rowData.id}`}>
          warning
        </span>
        <Tooltip
          target={`#warming-icon-${rowData.id}`}
          content={t(tooltipText)}
          className="tooltip--top"
          position="top"
          style={{ width: '220px' }}
          showDelay={5}
        />
      </>
    );
    const aiOverview = rowData && rowData.hasAiOverview && (
      <>
        <NavLink to={aiOverviewLink}>
          <span className="ico--ai-overview ico--sm" id={`ai-overview-${rowData.id}`} />
        </NavLink>
        <Tooltip
          target={`#ai-overview-${rowData.id}`}
          content={t('aiOverviewAvailable')}
          className="tooltip--top"
          position="top"
          showDelay={5}
        />
      </>
    );

    if (isOutOfRange(rowData.created)) {
      return (
        <div className="cell-warning cell-disable">
          <div id={`disable-prompt-${rowData.id}`}>{prompt}</div> {aiOverview} {indicator}
          <CustomTooltip
            tooltipText={t(isToday(rowData.created) ? 'outOfRangePromptToday' : 'outOfRangePrompt')}
            targetRef={`disable-prompt-${rowData.id}`}
          />
        </div>
      );
    }

    return rowData.status === 'PENDING' ? (
      <div className="cell-warning">
        {prompt}
        {aiOverview}
        {indicator}
      </div>
    ) : (
      <div>
        <div className="cell-warning">
          <NavLink to={getLinkToQuestion(defaultPeriodType, rowData.id)}>{prompt}</NavLink>
          {aiOverview}
          {indicator}
        </div>
      </div>
    );
  };

  const isToday = (created: Date | undefined) => {
    if (!created) {
      return false;
    }

    const today = new Date();
    const createdDate = new Date(created);

    return (
      createdDate.getFullYear() === today.getFullYear() &&
      createdDate.getMonth() === today.getMonth() &&
      createdDate.getDate() === today.getDate()
    );
  };

  const getLinkToQuestion = (defaultPeriodType: PeriodType, id: number | undefined) => {
    if (!id) {
      return '';
    }

    const basePath = `${PROJECTS}/${projectId}${QUESTIONS}/${id}/${getPeriodUrl(
      defaultPeriodType
    )}/${prepareAndGetChatType(accountResponse, 'ALL', chatType.toString()).toLowerCase()}`;
    const modelsParam = searchParams.get('models');
    return modelsParam ? `${basePath}?models=${modelsParam}` : basePath;
  };

  const isOutOfRange = (created: Date | undefined) =>
    range &&
    range[1] &&
    created &&
    new Date(created).getTime() >
      new Date(range[1].getFullYear(), range[1].getMonth(), range[1].getDate(), 23, 59, 59).getTime();

  const actionBodyTemplate = (rowData: PromptsData) => {
    // Bez prawa zapisu (dziś: gość) lecimy tą samą ścieżką co wiersz demo — zostaje sam podgląd
    // statystyk, bez edycji, pauzy i usuwania.
    const canEdit =
      projectsAccess.write &&
      (!rowData?.demoVersion ||
        (accountResponse !== null &&
          accountResponse.isSuperAdmin &&
          !accountResponse.isLimitedSuperAdmin));
    if (
      !canEdit ||
      (accountResponse !== null && ['BLOCKED', 'DISABLED'].includes(accountResponse?.accountStatus))
    ) {
      const defaultPeriodType = periodType === null ? rowData.defaultPeriod : periodType;

      return (
        <>
          <Button
            id={`stats-btn-${rowData.id}`}
            onClick={() => navigate(getLinkToQuestion(defaultPeriodType, rowData.id))}
            className="action-btn"
            disabled={rowData.status !== 'ACTIVE'}
          >
            <span className="ico--stats ico--xs" />
          </Button>
          <CustomTooltip tooltipText="viewDetails" targetRef={`stats-btn-${rowData.id}`} />
        </>
      );
    }

    return (
      <>
        <MoreBtn children={getMoreBtns(rowData)} rowId={rowData.id || 0} />
        {showGroups && rowData.id && onAssignToGroup && (() => {
          const availableGroupsForRow = Array.isArray(groups)
            ? groups.filter((g) => g.id !== rowData.promptGroupId)
            : [];
          const handleAssign = (groupId: number | null) => {
            const ref = moveMenuRefs.current.get(rowData.id!);
            ref?.hide({} as React.SyntheticEvent);
            onAssignToGroup(rowData.id!, groupId);
          };
          const menuModel = [
            ...(rowData.promptGroupId !== null && rowData.promptGroupId !== undefined
              ? [
                  {
                    template: () => (
                      <div className="move-to-group-menu__item" onClick={() => handleAssign(null)}>
                        <span>{t('untagged')}</span>
                      </div>
                    ),
                  },
                  ...(availableGroupsForRow.length > 0 ? [{ separator: true }] : []),
                ]
              : []),
            ...availableGroupsForRow.map((group) => ({
              template: () => (
                <div className="move-to-group-menu__item" onClick={() => handleAssign(group.id)}>
                  <span
                    className="move-to-group-menu__dot"
                    style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                  />
                  <span
                    id={`move-group-row-${rowData.id}-${group.id}`}
                    className="move-to-group-menu__name"
                  >
                    {group.name}
                  </span>
                  <Tooltip
                    target={`#move-group-row-${rowData.id}-${group.id}`}
                    content={group.name}
                    position="top"
                    showDelay={300}
                  />
                </div>
              ),
            })),
          ];
          return (
            <Menu
              ref={(el) => {
                if (el) moveMenuRefs.current.set(rowData.id!, el);
                else moveMenuRefs.current.delete(rowData.id!);
              }}
              popup
              appendTo={document.body}
              className="move-to-group-menu__popup"
              onHide={() => setOpenMoveMenuId(null)}
              model={menuModel}
            />
          );
        })()}
      </>
    );
  };

  // Why a paused row cannot be resumed right now: parked by the hub's project pause, or the project's
  // allocation is full. null = it can.
  const resumeBlock = (rowData: PromptsData): 'parked' | 'full' | null =>
    isParkedByHub(allocationProject, rowData) ? 'parked' : allocationFull ? 'full' : null;

  const getMoreBtns = (rowData: PromptsData) => {
    const defaultPeriodType = periodType === null ? rowData.defaultPeriod : periodType;

    return (
      <div className="buttons-list">
        {['PAUSED', 'ACTIVE'].includes(rowData.status) && !isOutOfRange(rowData.created) && (
          <>
            <Button
              id={`stats-btn-${rowData.id}`}
              onClick={() => {
                navigate(getLinkToQuestion(defaultPeriodType, rowData.id));
              }}
              className="action-btn"
            >
              <span className="ico--stats ico--xs" />
            </Button>
            <CustomTooltip tooltipText="viewDetails" targetRef={`stats-btn-${rowData.id}`} />
          </>
        )}
        {['PAUSED'].includes(rowData.status) && (
          <>
            {/* A resume the allocation rule would refuse stays clickable: the click explains why. */}
            <Button
              onClick={() => {
                if (resumeBlock(rowData) === 'parked') {
                  showProjectPaused(rowData.projectId ?? projectId ?? 0);
                } else if (resumeBlock(rowData) === 'full') {
                  showAllocationLimit(
                    rowData.projectId ?? projectId ?? 0,
                    promptsUsed,
                    allocationProject?.promptAllocation ?? 0
                  );
                } else {
                  toggleMonitor(rowData.id, rowData.status);
                }
              }}
              className={`action-btn${resumeBlock(rowData) ? ' btn--disabled-tooltip' : ''}`}
              id={`action-question-${rowData.id}`}
            >
              <span className="ico--run ico--xs" />
            </Button>
            <CustomTooltip
              tooltipText={
                resumeBlock(rowData) === 'parked'
                  ? 'projectPausedInHubTooltip'
                  : resumeBlock(rowData) === 'full'
                    ? 'allocationLimitTooltip'
                    : 'run'
              }
              options={{ used: promptsUsed, allocation: allocationProject?.promptAllocation ?? 0 }}
              targetRef={`action-question-${rowData.id}`}
            />
          </>
        )}
        {rowData.status === 'ACTIVE' && (
          <>
            <Button
              onClick={() => toggleMonitor(rowData.id, rowData.status)}
              className={`action-btn`}
              id={`action-question-${rowData.id}`}
            >
              <span className={`ico--pause ico--xs`} />
            </Button>
            <CustomTooltip tooltipText={'pause'} targetRef={`action-question-${rowData.id}`} />
          </>
        )}
        {showGroups && rowData.id && onAssignToGroup && (
          <>
            <Button
              id={`btn-move-prompt-${rowData.id}`}
              className="action-btn"
              onClick={(e) => {
                const ref = moveMenuRefs.current.get(rowData.id!);
                ref?.toggle(e);
                setOpenMoveMenuId(rowData.id!);
              }}
            >
              <span className="ico--move ico--xs" />
            </Button>
            <CustomTooltip tooltipText="moveToPromptGroup" targetRef={`btn-move-prompt-${rowData.id}`} />
          </>
        )}
        {rowData && rowData.id && (
          <>
            <Button
              onClick={() => confirmRemove(Number(rowData.id))}
              className={`action-btn`}
              id={`remove-question-${rowData.id}`}
            >
              <span className="ico--trash ico--xs" />
            </Button>
            <CustomTooltip
              tooltipText="removeThisQuestion"
              targetRef={`remove-question-${rowData.id}`}
            />
          </>
        )}
      </div>
    );
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  // Akcje masowe dla zaznaczonych promptów — w pasku narzędzi, a na telefonie (gdy pasek jest w górnej
  // belce) osobno na stronie.
  const renderBulkActions = () => {
    const hasAnyPaused = selectedPrompts.some((p) => {
      const current = data?.find((d) => d.id === p.id);
      return (current ?? p).status === 'PAUSED';
    });
    const hasAnyActive = selectedPrompts.some((p) => {
      const current = data?.find((d) => d.id === p.id);
      return (current ?? p).status === 'ACTIVE';
    });
    return (
      <>
        {hasAnyPaused && (
          <>
            <Button
              className="action-btn"
              onClick={() => bulkRunMutation.mutate()}
              disabled={bulkRunMutation.isPending}
              id="bulk-activate-btn"
            >
              <span className="ico--run ico--xs" />
            </Button>
            <CustomTooltip tooltipText="bulkActivate" targetRef="bulk-activate-btn" />
          </>
        )}
        {hasAnyActive && (
          <>
            <Button
              className={`action-btn`}
              onClick={() => confirmBulkPause()}
              disabled={bulkPauseMutation.isPending}
              id="bulk-pause-btn"
            >
              <span className="ico--pause ico--xs" />
            </Button>
            <CustomTooltip tooltipText={'bulkPause'} targetRef="bulk-pause-btn" />
          </>
        )}
        {showGroups && groups && groups.length > 0 && onBulkAssignToGroup && (
          <>
            <Button
              id="bulk-move-group-btn"
              className="action-btn"
              onClick={(e) => bulkMoveMenuRef.current?.toggle(e)}
            >
              <span className="ico--move ico--xs" />
            </Button>
            <CustomTooltip tooltipText="moveToPromptGroup" targetRef="bulk-move-group-btn" />
            <Menu
              ref={bulkMoveMenuRef}
              popup
              appendTo={document.body}
              className="move-to-group-menu__popup"
              model={[
                {
                  template: (
                    <div className="move-to-group-menu__list">
                      <div
                        className="move-to-group-menu__item"
                        onClick={() => {
                          bulkMoveMenuRef.current?.hide({} as React.SyntheticEvent);
                          onBulkAssignToGroup(
                            selectedPrompts.map((p) => p.id!).filter(Boolean),
                            null
                          );
                          setSelectedPrompts([]);
                        }}
                      >
                        <span>{t('untagged')}</span>
                      </div>
                      {groups.length > 0 && <div className="move-to-group-menu__separator" />}
                      {groups.map((group) => (
                        <div
                          key={group.id}
                          className="move-to-group-menu__item"
                          onClick={() => {
                            bulkMoveMenuRef.current?.hide({} as React.SyntheticEvent);
                            onBulkAssignToGroup(
                              selectedPrompts.map((p) => p.id!).filter(Boolean),
                              group.id
                            );
                            setSelectedPrompts([]);
                          }}
                        >
                          <span
                            className="move-to-group-menu__dot"
                            style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                          />
                          <span
                            id={`move-group-bulk-${group.id}`}
                            className="move-to-group-menu__name"
                          >
                            {group.name}
                          </span>
                          <Tooltip
                            target={`#move-group-bulk-${group.id}`}
                            content={group.name}
                            position="top"
                            showDelay={300}
                          />
                        </div>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          </>
        )}
        <>
          <Button
            className={`action-btn ${!canExportData ? 'btn--disabled-tooltip' : ''}`}
            onClick={handleExportSelectedCsv}
            id="bulk-export-csv-btn"
          >
            <span className={`ico--download ${!canExportData ? 'ico--disabled' : ''} ico--xs`} />
          </Button>
          <CustomTooltip
            tooltipText={!canExportData ? 'upgradePlanTrialExportMsg' : 'exportCsv'}
            targetRef="bulk-export-csv-btn"
          />
        </>
        <>
          <Button
            className="action-btn action-btn--danger"
            onClick={confirmBulkRemove}
            disabled={bulkRemoveMutation.isPending}
            id="bulk-remove-btn"
          >
            <span className="ico--trash ico--xs" />
          </Button>
          <CustomTooltip tooltipText="bulkRemove" targetRef="bulk-remove-btn" />
        </>
      </>
    );
  };

  // Telefon, tabela jako nagłówek strony (`stickyHeader`): akcje paska narzędzi w górnej belce —
  // wyszukiwarka, grupa, zaznaczanie i „nowe pytanie” (import z CSV zostaje na desktopie).
  const renderMobileHeaderActions = () => {
    if (!accountResponse || ['BLOCKED', 'DISABLED'].includes(accountResponse.accountStatus)) {
      return null;
    }
    const canEdit = !data?.some((promptData) => promptData.demoVersion) && projectsAccess.write;
    return (
      <>
        <MobileHeaderSearch
          value={globalFilterValue}
          onChange={(value) => onGlobalFilterChange({ target: { value } })}
        />
        {canEdit && onCreateGroup && (
          <button
            type="button"
            className="mobile-header__icon-btn"
            onClick={onCreateGroup}
            aria-label={t('createPromptGroup')}
          >
            <span className="ico--folder ico--sm" />
            <span className="mobile-header__icon-badge" />
          </button>
        )}
        {canEdit && (
          <>
            <button
              type="button"
              className={`mobile-header__icon-btn${selectionMode ? ' mobile-header__icon-btn--active' : ''}`}
              onClick={() => {
                setSelectionMode((prev) => {
                  if (prev) setSelectedPrompts([]);
                  return !prev;
                });
              }}
              aria-label={t('selectModePrompts')}
            >
              <span className="ico--select ico--sm" />
            </button>
            <button
              type="button"
              className="btn btn--sm mobile-header__add"
              disabled={!canAdd}
              onClick={() => navigate(`${PROJECTS}/${projectId}${QUESTIONS}${ADD}`)}
              aria-label={t('addNewQuestion')}
            >
              <span className="ico--add ico--xs" />
            </button>
          </>
        )}
      </>
    );
  };

  const renderButton = () => {
    if (!accountResponse || ['BLOCKED', 'DISABLED'].includes(accountResponse.accountStatus)) {
      return <></>;
    }

    const isDemoVersion = data?.some((promptData) => promptData.demoVersion);
    return (
      <div className="page-box__buttons prompts-table-toolbar">
        {headerExtra}
        <div className="page-box__buttons-panel">
          {selectionMode && selectedPrompts.length > 0 && renderBulkActions()}
        </div>
        <div className="input-search-box">
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search..."
            className="input-search"
          />
        </div>
        {!isDemoVersion && projectsAccess.write && (
          <>
            {accountResponse?.addons?.includes('IMPORT_FROM_CSV') ? (
              <SplitButton
                ref={splitButtonRef}
                className={`btn btn--sm ${!canAdd ? 'btn--disabled-tooltip-color' : ''}`}
                onClick={() => navigate(`${PROJECTS}/${projectId}${QUESTIONS}${ADD}`)}
                label={t('addNewQuestion')}
                id="add-prompt"
                icon={<span className="ico--add ico--xs" />}
                disabled={!canAdd}
                model={[
                  {
                    label: t('importFromCsv'),
                    icon: <span className="ico--download ico--xs" />,
                    command: () => setImportDialogVisible(true),
                  },
                ]}
              />
            ) : (
              <Button
                className={`btn btn--sm ${!canAdd ? 'btn--disabled-tooltip-color' : ''}`}
                onClick={() => {
                  if (canAdd) {
                    navigate(`${PROJECTS}/${projectId}${QUESTIONS}${ADD}`);
                  }
                }}
                label={t('addNewQuestion')}
                id="add-prompt"
                icon={<span className="ico--add ico--xs" />}
              />
            )}
            {!canAdd && (
              <LimitTooltipComponent
                id="add-prompt"
                direction="bottom"
                // The account limit's text unless it is the project's allocation that is full.
                text={
                  canManage && allocationFull
                    ? t('allocationLimitTooltip', {
                        used: promptsUsed,
                        allocation: allocationProject?.promptAllocation ?? 0,
                      })
                    : undefined
                }
              />
            )}
          </>
        )}
        {!isDemoVersion && projectsAccess.write && onCreateGroup && (
          <>
            <Button
              className={`btn btn--sm btn--secondary`}
              onClick={onCreateGroup}
              label={t('createPromptGroup')}
              id="btn-create-prompt-group"
              icon={<span className="ico--add ico--xs" />}
            />
            <CustomTooltip tooltipText="createPromptGroupTooltip" targetRef="btn-create-prompt-group" />
          </>
        )}
        {/* Tryb zaznaczania istnieje wyłącznie po to, żeby hurtem uruchomić, zapauzować, przenieść
            lub usunąć prompty — same zapisy, więc bez `PROJECTS.write` przycisk nie ma po co być. */}
        {!isDemoVersion && projectsAccess.write && (
          <>
            <Button
              className={`action-btn ${selectionMode ? ' action-btn--active' : ''}`}
              onClick={() => {
                setSelectionMode((prev) => {
                  if (prev) setSelectedPrompts([]);
                  return !prev;
                });
              }}
              id="select-mode-btn"
            >
              <span className="ico--select ico--xs" />
            </Button>
            <CustomTooltip tooltipText="selectModePrompts" targetRef="select-mode-btn" />
          </>
        )}
      </div>
    );
  };

  const searchVolumeBodyTemplate = (rowData: PromptsData) => {
    const additionalPanel =
      accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin ? (
        <div className="additional-panel">{rowData.searchKeyword}</div>
      ) : (
        <></>
      );

    return rowData.searchVolume === undefined ? (
      <>
        <span id={`no-search-volume-${rowData.id}`} className="table__cell-loader" />
        <CustomTooltip
          tooltipText={t('waitingSearchVolume')}
          targetRef={`no-search-volume-${rowData.id}`}
        />
        {additionalPanel}
      </>
    ) : rowData.searchVolume === '<100' ? (
      <div className="search-volume">
        <div
          id={`no-search-volume-${rowData.id}`}
          className="search-volume__value search-volume__value--less"
        >
          {rowData.searchVolume}
        </div>
        <CustomTooltip tooltipText={t('noSearchVolume')} targetRef={`no-search-volume-${rowData.id}`} />
        {additionalPanel}
      </div>
    ) : (
      <div className="search-volume">
        {isNaN(Number(rowData.searchVolume)) ? (
          <>- {additionalPanel}</>
        ) : (
          <div className="search-volume__value search-volume__value--more">
            {Intl.NumberFormat('pl-PL').format(Number(rowData.searchVolume))}
          </div>
        )}
        {additionalPanel}
      </div>
    );
  };

  const positionCellBodyTemplate = (
    rowData: PromptsData,
    field: keyof PromptsData,
    modelKey?: string
  ) => {
    const isModelMissingData = !!modelKey && !!modelsWithoutData && modelsWithoutData.includes(modelKey);

    if (isLoading) {
      return <span className="skeleton--cell" />;
    }
    // Model/prompt still collecting → a small spinner, not a skeleton bar (reads as "in progress").
    if (isModelMissingData || rowData.status === 'PENDING') {
      return <span className="cell-spinner" aria-hidden="true" />;
    }

    const val = rowData[field];
    let content: React.ReactNode;
    if (!val) {
      content = '-';
    } else {
      const num = parseFloat(val as string);
      content = isNaN(num) ? String(val) : num % 1 === 0 ? String(num) : String(val);
    }

    // Archival model (historical data, model no longer on the account): grey out the cell text.
    if (modelKey && isHistoricalModel(modelKey)) {
      return <span className="prompts-table__archival-cell">{content}</span>;
    }
    return content;
  };

  // "ALL" Average Position cell. Primary (top) = the filter-respecting value (unchanged from before);
  // below it, in grey parentheses, the all-models value incl. archival — only for NEW_STATS when they differ.
  const renderAveragePositionCell = (rowData: PromptsData): React.ReactNode => {
    if (isLoading) {
      return <span className="skeleton--cell" />;
    }
    if (rowData.status === 'PENDING') {
      return <span className="cell-spinner" aria-hidden="true" />;
    }
    const primaryVal = rowData.averagePosition; // filter-respecting (matches the legacy value)
    const allVal = rowData.averagePositionAll; // incl. archival
    const hasPrimary = primaryVal != null && primaryVal !== '-' && (primaryVal as string) !== '';
    const hasAll = allVal != null && allVal !== '-' && allVal !== '';
    const primary = hasPrimary ? primaryVal : hasAll ? allVal : '-';
    const showArchival = hasNewStats && hasPrimary && hasAll && String(allVal) !== String(primaryVal);
    if (!showArchival) {
      return <>{primary}</>;
    }
    // Archival models present: for now show only the all-models value (incl. archival) as the main
    // one — drop the filter-respecting "first" line.
    return <>{allVal}</>;
  };

  // Same skeleton-while-collecting behaviour as the per-model position cells, for the Visibility column.
  const visibilityCellBodyTemplate = (rowData: PromptsData): React.ReactNode => {
    if (isLoading) {
      return <span className="skeleton--cell" />;
    }
    if (rowData.status === 'PENDING') {
      return <span className="cell-spinner" aria-hidden="true" />;
    }
    const all = rowData.visibility; // incl. archival
    const current = rowData.visibilityCurrent; // account only
    const hasAll = all != null;
    const hasCurrent = current != null && current !== '';
    if (!hasAll && !hasCurrent) {
      return '-';
    }
    const primary = hasCurrent ? `${current}%` : `${all}%`;
    const showArchival = hasNewStats && hasAll && hasCurrent && String(all) !== String(current);
    if (!showArchival) {
      return primary;
    }
    // Archival models present: for now show only the all-models value (incl. archival) as the main
    // one — drop the account-only "first" line.
    return `${all}%`;
  };

  const emptyTemplate = () => {
    return <NoResults />;
  };

  const emptyGroupTemplate = () => {
    return (
      <div className="no-results no-results--compact">
        <div className="no-results__title">{globalFilterValue ? t('noResultsFound') : t('emptyGroup')}</div>
      </div>
    );
  };

  if (data === null) {
    return (
      <>
        <Title showLoader={true} />

        <div className={`table-content ${additionalClass ? additionalClass : ''}`}>
          <Skeleton className="skeleton--table">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </Skeleton>
        </div>
      </>
    );
  }

  if (!data) {
    return <></>;
  }

  const canDragPrompts = !!(onAssignToGroup && showGroups);

  const handleRowDragStart = (e: React.DragEvent) => {
    if (!canDragPrompts) return;
    const tr = (e.target as HTMLElement).closest('tr');
    if (!tr) return;
    const marker = tr.querySelector<HTMLElement>('[data-prompt-id]');
    if (!marker) return;
    const promptId = marker.dataset.promptId;
    if (!promptId) return;
    e.dataTransfer.setData('application/prompt-id', promptId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedPromptId(Number(promptId));

    const ghost = document.createElement('div');
    ghost.className = 'prompt-drag-ghost';
    const promptData = data?.find((d) => d.id === Number(promptId));
    ghost.textContent = promptData?.prompt || `Prompt #${promptId}`;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  };

  const renderDataTable = (tableData: PromptsData[], tableKey: string, isGroupChild = false) => (
    <DataTable
      key={tableKey}
      stripedRows
      paginator={true}
      scrollable
      scrollHeight={isGroupChild || isMobile ? undefined : scrollHeight}
      paginatorClassName="pagination"
      removableSort
      rows={10}
      rowsPerPageOptions={[10, 25, 50]}
      value={tableData}
      tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
      tableClassName={`table ${selectionMode ? 'table--select' : ''}${canDragPrompts ? ' table--draggable-rows' : ''}`}
      className="table-wrapper table-wrapper--stack"
      responsiveLayout="stack"
      filters={filters}
      emptyMessage={isGroupChild ? emptyGroupTemplate : emptyTemplate}
      currentPageReportTemplate={t('rowsNumber') + ':'}
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      sortField={'averagePosition'}
      sortOrder={1}
      rowClassName={(rowData: PromptsData) =>
        draggedPromptId && rowData.id === draggedPromptId ? 'prompt-row--dragging' : ''
      }
    >
      {selectionMode && (
        <Column
          exportable={false}
          headerStyle={{ width: '25px' }}
          bodyClassName="table__cell--stack-select"
          header={
            <Checkbox
              checked={tableData.length > 0 && tableData.every((r) => selectedPrompts.some((p) => p.id === r.id))}
              onChange={(e) =>
                setSelectedPrompts((prev) =>
                  e.checked
                    ? [...prev.filter((p) => !tableData.some((r) => r.id === p.id)), ...tableData]
                    : prev.filter((p) => !tableData.some((r) => r.id === p.id))
                )
              }
            />
          }
          body={(rowData: PromptsData) => (
            <Checkbox
              checked={selectedPrompts.some((p) => p.id === rowData.id)}
              onChange={(e) => {
                if (e.checked) {
                  setSelectedPrompts((prev) => [...prev, rowData]);
                } else {
                  setSelectedPrompts((prev) => prev.filter((p) => p.id !== rowData.id));
                }
              }}
            />
          )}
        />
      )}
          <Column
            body={statusBodyTemplate}
            header={t('status')}
            headerClassName="table__header"
            bodyClassName="prompts-table__status-cell"
            style={{ width: '37px' }}
          />
          <Column
            sortable
            field="prompt"
            header={t('prompt')}
            body={promptBodyTemplate}
            bodyClassName="table__cell--stack-title"
            headerClassName="table__header"
            style={{ minWidth: '200px', maxWidth: '100%' }}
          />
          {showKeyword && createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            field: 'keyword',
            headerName: 'keyword',
            tooltip: t('keywordTooltip'),
            headerClassName: 'table__header',
            bodyClassName: 'table__cell--search-volume',
            style: { width: '120px' },
            body: keywordBodyTemplate,
          })}
          {showStats && createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            field: 'searchVolume',
            headerName: 'searchVolume',
            tooltip: t('searchVolumeTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell--search-volume table__cell--center',
            style: { width: '60px' },
            body: searchVolumeBodyTemplate,
            hidden: chatType !== 'ALL',
          })}
          {showStats && (
          <Column
            field={'averagePosition'}
            header={t('averagePosition')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(data) => renderAveragePositionCell(data)}
            style={{ width: '125px' }}
            hidden={chatType === 'ALL'}
          />
          )}
          {showStats && createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            field: 'averagePosition',
            headerName: 'averagePosition',
            tooltip: t('overallPositionTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell--center',
            style: { width: '60px' },
            body: (rowData: any) => renderAveragePositionCell(rowData),
            hidden: chatType !== 'ALL',
          })}
          {showStats && showVisibility && (
          <Column
            sortable
            field={'visibility'}
            header={t('visibility')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: PromptsData) => <>{visibilityCellBodyTemplate(rowData)}</>}
            style={{ width: '90px' }}
          />
          )}
          {showFeaturesColumns && (
          <Column
            sortable
            field={'chatgptAdsPercent'}
            header={t('chatgptAds') + ' %'}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: PromptsData) =>
              rowData.chatgptAdsPercent != null ? `${rowData.chatgptAdsPercent}%` : '-'
            }
            style={{ width: '95px' }}
          />
          )}
          {showFeaturesColumns && (
          <Column
            sortable
            field={'shoppingCardsPercent'}
            header={t('shoppingCards') + ' %'}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: PromptsData) =>
              rowData.shoppingCardsPercent != null ? `${rowData.shoppingCardsPercent}%` : '-'
            }
            style={{ width: '105px' }}
          />
          )}
          {showStats && (
          <Column
            field={'averageGPTPosition'}
            header={renderModelHeader('GPT', 'gptName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => <>{positionCellBodyTemplate(rowData, 'averageGPTPosition', 'GPT')}</>}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('GPT')}
          />
          )}
          {showStats && (
          <Column
            field={'averageClaudePosition'}
            header={renderModelHeader('CLAUDE', 'claudeName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageClaudePosition', 'CLAUDE')}</>
            )}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('CLAUDE')}
          />
          )}
          {showStats && (
          <Column
            field={'averageGeminiPosition'}
            header={renderModelHeader('GEMINI', 'geminiName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageGeminiPosition', 'GEMINI')}</>
            )}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('GEMINI')}
          />
          )}
          {showStats && (
          <Column
            field={'averageDeepSeekPosition'}
            header={renderModelHeader('DEEP_SEEK', 'deepSeekName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageDeepSeekPosition', 'DEEP_SEEK')}</>
            )}
            style={{ width: '52px' }}
            hidden={isModelColumnHidden('DEEP_SEEK')}
          />
          )}
          {showStats && (
          <Column
            field={'averagePerplexityPosition'}
            header={renderModelHeader('PERPLEXITY', 'perplexityName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averagePerplexityPosition', 'PERPLEXITY')}</>
            )}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('PERPLEXITY')}
          />
          )}
          {showStats && (
          <Column
            field={'averageCopilotPosition'}
            header={renderModelHeader('COPILOT', 'copilotSearchName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageCopilotPosition', 'COPILOT')}</>
            )}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('COPILOT')}
          />
          )}
          {showStats && (
          <Column
            field={'averageGrokPosition'}
            header={renderModelHeader('GROK', 'grokSearchName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => <>{positionCellBodyTemplate(rowData, 'averageGrokPosition', 'GROK')}</>}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('GROK')}
          />
          )}
          {showStats && (
          <Column
            field={'averageAiOverviewPosition'}
            header={renderModelHeader('AI_OVERVIEW_NEW', 'aiOverviewName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageAiOverviewPosition', 'AI_OVERVIEW_NEW')}</>
            )}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('AI_OVERVIEW_NEW')}
          />
          )}
          {showStats && (
          <Column
            field={'averageAiModePosition'}
            header={renderModelHeader('AI_MODE', 'aiModeName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageAiModePosition', 'AI_MODE')}</>
            )}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('AI_MODE')}
          />
          )}
          {showStats && (
          <Column
            field={'shareOfVoice'}
            header={t('averageShareOfVoice')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '160px' }}
            hidden={chatType === 'ALL'}
          />
          )}
          <Column
            frozen={true}
            alignFrozen="right"
            header={t('action')}
            body={actionBodyTemplate}
            bodyClassName="table__cell--gap table__cell--stack-actions"
            headerClassName="table__header table__header--center"
            align={'center'}
            style={{ width: '34px' }}
          />
    </DataTable>
  );

  const handleGroupDragStart = (e: React.DragEvent, groupId: number) => {
    setDraggedGroupId(groupId);
    e.dataTransfer.setData('application/group-id', String(groupId));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleGroupDragOver = (e: React.DragEvent, groupId: number) => {
    if (!e.dataTransfer.types.includes('application/group-id')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverGroupId !== groupId) {
      setDragOverGroupId(groupId);
    }
  };

  const handleGroupDrop = (e: React.DragEvent, targetGroupId: number) => {
    if (!e.dataTransfer.types.includes('application/group-id')) return;
    e.preventDefault();
    setDragOverGroupId(null);
    if (!draggedGroupId || draggedGroupId === targetGroupId || !onReorderGroups || !groups) {
      setDraggedGroupId(null);
      return;
    }
    const ids = groups.map((g) => g.id);
    const fromIndex = ids.indexOf(draggedGroupId);
    const toIndex = ids.indexOf(targetGroupId);
    if (fromIndex === -1 || toIndex === -1) {
      setDraggedGroupId(null);
      return;
    }
    const newIds = [...ids];
    newIds.splice(fromIndex, 1);
    newIds.splice(toIndex, 0, draggedGroupId);
    onReorderGroups(newIds);
    setDraggedGroupId(null);
  };

  const handleGroupDragEnd = () => {
    setDraggedGroupId(null);
    setDragOverGroupId(null);
  };

  const handlePromptDragOver = (e: React.DragEvent, groupId: number | 'untagged') => {
    if (!e.dataTransfer.types.includes('application/prompt-id')) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverGroupIdForPrompt !== groupId) {
      setDragOverGroupIdForPrompt(groupId);
    }
  };

  const handlePromptDrop = (e: React.DragEvent, targetGroupId: number | 'untagged') => {
    const promptIdStr = e.dataTransfer.getData('application/prompt-id');
    if (!promptIdStr || !onAssignToGroup) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroupIdForPrompt(null);
    const promptId = Number(promptIdStr);
    const newGroupId = targetGroupId === 'untagged' ? null : targetGroupId;
    onAssignToGroup(promptId, newGroupId);
  };

  const handlePromptDragEnd = () => {
    setDragOverGroupIdForPrompt(null);
    setDraggedPromptId(null);
  };


  const renderAccordionContent = () => {
    if (!accordionData) return null;

    const toggleGroup = (groupId: number) => {
      const isOpen = expandedAccordionGroups.has(groupId);
      if (!isOpen) {
        // Expand: splice children in. Cancel any pending collapse for this group.
        const tid = collapseTimers.current.get(groupId);
        if (tid) {
          clearTimeout(tid);
          collapseTimers.current.delete(groupId);
        }
        setCollapsingGroups((prev) => {
          if (!prev.has(groupId)) return prev;
          const n = new Set(prev);
          n.delete(groupId);
          return n;
        });
        setExpandedAccordionGroups((prev) => {
          const n = new Set(prev);
          n.add(groupId);
          return n;
        });
        setRecentlyExpandedGroup(groupId);
        if (expandAnimTimer.current !== null) clearTimeout(expandAnimTimer.current);
        expandAnimTimer.current = setTimeout(() => {
          setRecentlyExpandedGroup((cur) => (cur === groupId ? null : cur));
          expandAnimTimer.current = null;
        }, 300);
      } else {
        // Collapse: keep in expandedAccordionGroups so reveal animation plays, then unmount (~200ms).
        setCollapsingGroups((prev) => {
          const n = new Set(prev);
          n.add(groupId);
          return n;
        });
        const tid = setTimeout(() => {
          collapseTimers.current.delete(groupId);
          setExpandedAccordionGroups((prev) => {
            const n = new Set(prev);
            n.delete(groupId);
            return n;
          });
          setCollapsingGroups((prev) => {
            const n = new Set(prev);
            n.delete(groupId);
            return n;
          });
        }, 200);
        collapseTimers.current.set(groupId, tid);
      }
    };

    // Group rows are NOT tinted with the tag colour anymore — the tag colour is shown as a small dot
    // next to the group name instead. Group rows just use the neutral `.accordion-group-summary-row`
    // background to read as section headers.
    const reveal = (content: React.ReactNode) => (
      <div className="accordion-reveal"><div className="accordion-reveal__inner">{content}</div></div>
    );

    return (
      <div className={`table-content table-content--accordion${scrollHeight ? ' table-content--simplebar' : ''} ${additionalClass ? additionalClass : ''}`}>
        <div className="accordion-table-frame">
        <SimpleBarScroll enabled={!!scrollHeight && !isMobile} maxHeight={scrollHeight}>
        <DataTable
          scrollable
          lazy
          removableSort
          sortField={accordionSortField ?? undefined}
          sortOrder={accordionSortOrder ?? 0}
          onSort={(e) => {
            setAccordionSortField(e.sortField || null);
            setAccordionSortOrder((e.sortOrder as 1 | -1 | null) || null);
            setAccordionFirst(0);
          }}
          value={accordionPageRows}
          dataKey="id"
          tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
          tableClassName={`table table--accordion-groups${selectionMode ? ' table--select' : ''}`}
          className="table-wrapper table-wrapper--accordion table-wrapper--stack"
          responsiveLayout="stack"
          emptyMessage={emptyTemplate}
          rowClassName={(rowData: any) => {
            if (rowData._isHeaderRow) return 'accordion-subheader-row';
            if (rowData._isGroupRow)
              return `accordion-group-summary-row${rowData._groupEven ? ' accordion-group-summary-row--alt' : ''}${
                expandedAccordionGroups.has(rowData._groupId) ? ' accordion-group-summary-row--expanded' : ''
              }`;
            const gid = rowData.promptGroupId;
            // Zebra striping only for UNGROUPED prompts (gid == null). Prompts inside an expanded
            // accordion group stay white — only the group-summary header row carries colour.
            // `accordion-group-child(--last)` — na telefonie pytania grupy leżą w karcie grupy.
            const mods =
              (rowData._zebraEven && gid == null ? ' accordion-child-even' : '') +
              ('_groupLast' in rowData ? ' accordion-group-child' : '') +
              ('_groupLast' in rowData && rowData._zebraEven ? ' accordion-group-child--even' : '') +
              (rowData._groupLast ? ' accordion-group-child--last' : '');
            if (gid != null && collapsingGroups.has(gid)) return `accordion-prompt-row accordion-row--leave${mods}`;
            if (gid != null && gid === recentlyExpandedGroup) return `accordion-prompt-row accordion-row--enter${mods}`;
            return `accordion-prompt-row${mods}`;
          }}
        >
          {selectionMode && (
            <Column
              exportable={false}
              headerStyle={{ width: '25px' }}
              bodyClassName="table__cell--stack-select"
              header={(() => {
                const selectable = accordionPageRows.filter((r) => !r._isGroupRow && !r._isHeaderRow);
                return (
                  <Checkbox
                    checked={
                      selectable.length > 0 &&
                      selectable.every((r) => selectedPrompts.some((p) => p.id === r.id))
                    }
                    onChange={(e) =>
                      setSelectedPrompts((prev) =>
                        e.checked
                          ? [...prev.filter((p) => !selectable.some((r) => r.id === p.id)), ...selectable]
                          : prev.filter((p) => !selectable.some((r) => r.id === p.id))
                      )
                    }
                  />
                );
              })()}
              body={(rowData: any) => {
                if (rowData._isHeaderRow) return null;
                // Group-summary row: one checkbox toggles ALL of the group's prompts (even collapsed ones).
                if (rowData._isGroupRow) {
                  const children: PromptsData[] = rowData._childPrompts || [];
                  const allSelected =
                    children.length > 0 &&
                    children.every((c) => selectedPrompts.some((p) => p.id === c.id));
                  return (
                    <Checkbox
                      checked={allSelected}
                      onChange={(e) =>
                        setSelectedPrompts((prev) =>
                          e.checked
                            ? [...prev.filter((p) => !children.some((c) => c.id === p.id)), ...children]
                            : prev.filter((p) => !children.some((c) => c.id === p.id))
                        )
                      }
                    />
                  );
                }
                return reveal(
                  <Checkbox
                    checked={selectedPrompts.some((p) => p.id === rowData.id)}
                    onChange={(e) => {
                      if (e.checked) {
                        setSelectedPrompts((prev) => [...prev, rowData]);
                      } else {
                        setSelectedPrompts((prev) => prev.filter((p) => p.id !== rowData.id));
                      }
                    }}
                  />
                );
              }}
            />
          )}
          <Column
            sortable
            field="prompt"
            header={t('prompt')}
            headerClassName="table__header"
            bodyClassName="table__cell--stack-title"
            style={{ minWidth: '200px', maxWidth: '100%' }}
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('prompt')}</span>;
              if (rowData._isGroupRow) {
                return (
                  <div className="accordion-group-row">
                    <span
                      className="accordion-group-row__dot"
                      style={{ backgroundColor: rowData._groupColor || 'var(--grey-ux-400)' }}
                    />
                    <span className="accordion-group-row__name">{rowData._groupName}</span>
                    <span className="accordion-group-row__count">{rowData._childCount}</span>
                  </div>
                );
              }
              // Status indicator dot moved inline next to the prompt (the standalone Status column
              // was removed from the accordion for a cleaner single-table look).
              return reveal(
                <div className="accordion-prompt-cell">
                  {statusBodyTemplate(rowData as PromptsData)}
                  {promptBodyTemplate(rowData as PromptsData)}
                </div>
              );
            }}
          />
          {showStats && createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            field: 'searchVolume',
            headerName: 'searchVolume',
            tooltip: t('searchVolumeTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell--search-volume table__cell--center',
            style: { width: '60px' },
            body: (rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('searchVolume')}</span>;
              if (rowData._isGroupRow) return null; // search volume is not aggregated for groups
              return reveal(<>{searchVolumeBodyTemplate(rowData as PromptsData)}</>);
            },
            hidden: chatType !== 'ALL',
          })}
          {showStats && (
          <Column
            field={'averagePosition'}
            header={t('averagePosition')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('averagePosition')}</span>;
              if (rowData._isGroupRow) return renderAveragePositionCell(rowData as PromptsData);
              return reveal(<>{renderAveragePositionCell(rowData as PromptsData)}</>);
            }}
            style={{ width: '125px' }}
            hidden={chatType === 'ALL'}
          />
          )}
          {showStats && createCustomColumn({
            plainHeader: isMobile,
            sortable: true,
            field: 'averagePosition',
            headerName: 'averagePosition',
            tooltip: t('overallPositionTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell--center',
            style: { width: '60px' },
            body: (rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('averagePosition')}</span>;
              if (rowData._isGroupRow) return renderAveragePositionCell(rowData as PromptsData);
              return reveal(<>{renderAveragePositionCell(rowData as PromptsData)}</>);
            },
            hidden: chatType !== 'ALL',
          })}
          {showStats && showVisibility && (
          <Column
            sortable
            field={'visibility'}
            header={t('visibility')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('visibility')}</span>;
              if (rowData._isGroupRow) return visibilityCellBodyTemplate(rowData as PromptsData);
              return reveal(<>{visibilityCellBodyTemplate(rowData as PromptsData)}</>);
            }}
            style={{ width: '90px' }}
          />
          )}
          {showFeaturesColumns && (
          <Column
            sortable
            field={'chatgptAdsPercent'}
            header={t('chatgptAds') + ' %'}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow)
                return <span className="accordion-subheader-label">{t('chatgptAds')} %</span>;
              const value = (rowData as PromptsData).chatgptAdsPercent;
              const cell = value != null ? `${value}%` : '-';
              return rowData._isGroupRow ? cell : reveal(<>{cell}</>);
            }}
            style={{ width: '95px' }}
          />
          )}
          {showFeaturesColumns && (
          <Column
            sortable
            field={'shoppingCardsPercent'}
            header={t('shoppingCards') + ' %'}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow)
                return <span className="accordion-subheader-label">{t('shoppingCards')} %</span>;
              const value = (rowData as PromptsData).shoppingCardsPercent;
              const cell = value != null ? `${value}%` : '-';
              return rowData._isGroupRow ? cell : reveal(<>{cell}</>);
            }}
            style={{ width: '105px' }}
          />
          )}
          {showStats && (
          <Column
            field={'averageGPTPosition'}
            header={renderModelHeader('GPT', 'gptName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('gptName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averageGPTPosition, 'GPT');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageGPTPosition', 'GPT')}</>);
            }}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('GPT')}
          />
          )}
          {showStats && (
          <Column
            field={'averageClaudePosition'}
            header={renderModelHeader('CLAUDE', 'claudeName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('claudeName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averageClaudePosition, 'CLAUDE');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageClaudePosition', 'CLAUDE')}</>);
            }}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('CLAUDE')}
          />
          )}
          {showStats && (
          <Column
            field={'averageGeminiPosition'}
            header={renderModelHeader('GEMINI', 'geminiName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('geminiName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averageGeminiPosition, 'GEMINI');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageGeminiPosition', 'GEMINI')}</>);
            }}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('GEMINI')}
          />
          )}
          {showStats && (
          <Column
            field={'averageDeepSeekPosition'}
            header={renderModelHeader('DEEP_SEEK', 'deepSeekName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('deepSeekName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averageDeepSeekPosition, 'DEEP_SEEK');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageDeepSeekPosition', 'DEEP_SEEK')}</>);
            }}
            style={{ width: '52px' }}
            hidden={isModelColumnHidden('DEEP_SEEK')}
          />
          )}
          {showStats && (
          <Column
            field={'averagePerplexityPosition'}
            header={renderModelHeader('PERPLEXITY', 'perplexityName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('perplexityName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averagePerplexityPosition, 'PERPLEXITY');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averagePerplexityPosition', 'PERPLEXITY')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('PERPLEXITY')}
          />
          )}
          {showStats && (
          <Column
            field={'averageCopilotPosition'}
            header={renderModelHeader('COPILOT', 'copilotSearchName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('copilotSearchName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averageCopilotPosition, 'COPILOT');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageCopilotPosition', 'COPILOT')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('COPILOT')}
          />
          )}
          {showStats && (
          <Column
            field={'averageGrokPosition'}
            header={renderModelHeader('GROK', 'grokSearchName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('grokSearchName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averageGrokPosition, 'GROK');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageGrokPosition', 'GROK')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('GROK')}
          />
          )}
          {showStats && (
          <Column
            field={'averageAiOverviewPosition'}
            header={renderModelHeader('AI_OVERVIEW_NEW', 'aiOverviewName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('aiOverviewName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averageAiOverviewPosition, 'AI_OVERVIEW_NEW');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageAiOverviewPosition', 'AI_OVERVIEW_NEW')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('AI_OVERVIEW_NEW')}
          />
          )}
          {showStats && (
          <Column
            field={'averageAiModePosition'}
            header={renderModelHeader('AI_MODE', 'aiModeName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('aiModeName')}</span>;
              if (rowData._isGroupRow) return groupModelCell(rowData.averageAiModePosition, 'AI_MODE');
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageAiModePosition', 'AI_MODE')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('AI_MODE')}
          />
          )}
          {showStats && (
          <Column
            field={'shareOfVoice'}
            header={t('averageShareOfVoice')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('averageShareOfVoice')}</span>;
              if (rowData._isGroupRow) return <>{rowData.shareOfVoice ? `${rowData.shareOfVoice}%` : '-'}</>;
              return reveal(<>{(rowData as PromptsData).shareOfVoice || '-'}</>);
            }}
            style={{ width: '160px' }}
            hidden={chatType === 'ALL'}
          />
          )}
          <Column
            frozen={true}
            alignFrozen="right"
            header={t('action')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--gap table__cell--stack-actions"
            align={'center'}
            style={{ width: '34px' }}
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('action')}</span>;
              if (rowData._isGroupRow) {
                // While collapsing, flip the chevron immediately (the group is still in
                // expandedAccordionGroups so its children can finish animating out).
                const isExpanded =
                  expandedAccordionGroups.has(rowData._groupId) &&
                  !collapsingGroups.has(rowData._groupId);
                return (
                  <button
                    type="button"
                    aria-label="toggle group"
                    className={`accordion-group-toggle${isExpanded ? ' accordion-group-toggle--expanded' : ''}`}
                    onClick={() => toggleGroup(rowData._groupId)}
                  >
                    <span className="accordion-group-toggle__chevron ico--up ico--md" />
                  </button>
                );
              }
              return reveal(<>{actionBodyTemplate(rowData as PromptsData)}</>);
            }}
          />
        </DataTable>
        </SimpleBarScroll>
        <Paginator
          className="pagination"
          first={accordionFirst}
          rows={accordionRows}
          totalRecords={accordionUnits.length}
          rowsPerPageOptions={[10, 25, 50, 100]}
          currentPageReportTemplate={t('rowsNumber') + ':'}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          onPageChange={(e: PaginatorPageChangeEvent) => {
            setAccordionFirst(e.first);
            setAccordionRows(e.rows);
          }}
        />
        </div>
      </div>
    );
  };

  const renderGroupedContent = () => {
    const ungroupedPrompts = promptsByGroup.get(null) || [];
    const canDrag = !!onReorderGroups;
    return (
      <div
        className={`table-content ${additionalClass ? additionalClass : ''}`}
        ref={groupedContentRef}
        onDragStart={handleRowDragStart}
        onDragEnd={handlePromptDragEnd}
      >
        {groups!.map((group) => {
          const groupPrompts = promptsByGroup.get(group.id) || [];
          const isCollapsed = collapsedGroups.has(group.id);
          const toggleCollapse = () =>
            setCollapsedGroups((prev) => {
              const next = new Set(prev);
              if (next.has(group.id)) next.delete(group.id);
              else next.add(group.id);
              return next;
            });
          const isDragging = draggedGroupId === group.id;
          const isDragOver = dragOverGroupId === group.id && draggedGroupId !== group.id;
          const isPromptDragOver = dragOverGroupIdForPrompt === group.id;
          return (
            <div
              className={`prompt-group${isDragging ? ' prompt-group--dragging' : ''}${
                isDragOver || isPromptDragOver ? ' prompt-group--drag-over' : ''
              }`}
              key={`prompt-group-${group.id}`}
              onDragOver={(e) => {
                if (canDrag) handleGroupDragOver(e, group.id);
                handlePromptDragOver(e, group.id);
              }}
              onDrop={(e) => {
                if (canDrag) handleGroupDrop(e, group.id);
                handlePromptDrop(e, group.id);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setDragOverGroupIdForPrompt(null);
                }
              }}
            >
              <div
                draggable={canDrag}
                onDragStart={
                  canDrag
                    ? (e) => {
                        e.stopPropagation();
                        handleGroupDragStart(e, group.id);
                      }
                    : undefined
                }
                onDragEnd={canDrag ? handleGroupDragEnd : undefined}
                className={canDrag ? 'prompt-group__header-drag' : undefined}
              >
                {renderGroupHeader ? (
                  renderGroupHeader(group, groupPrompts.length, isCollapsed, toggleCollapse)
                ) : (
                  <div
                    className={`project-group-header${
                      isCollapsed ? ' project-group-header--collapsed' : ''
                    }`}
                    onClick={toggleCollapse}
                  >
                    <div className="project-group-header__left">
                      <span className="ico--up ico--xs project-group-header__chevron" />
                      <span
                        className="project-group-header__dot"
                        style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                      />
                      <span className="project-group-header__name">{group.name}</span>
                      <span className="project-group-header__count">{groupPrompts.length}</span>
                    </div>
                  </div>
                )}
              </div>
              {!isCollapsed && renderDataTable(groupPrompts, `table-prompt-group-${group.id}`, true)}
            </div>
          );
        })}
        {ungroupedPrompts.length > 0 && (
          <div
            className={`prompt-group${
              dragOverGroupIdForPrompt === 'untagged' ? ' prompt-group--drag-over' : ''
            }`}
            onDragOver={(e) => handlePromptDragOver(e, 'untagged')}
            onDrop={(e) => handlePromptDrop(e, 'untagged')}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setDragOverGroupIdForPrompt(null);
              }
            }}
          >
            <div
              className={`project-group-header project-group-header--ungrouped${
                ungroupedCollapsed ? ' project-group-header--collapsed' : ''
              }`}
              onClick={() => setUngroupedCollapsed((prev) => !prev)}
            >
              <div className="project-group-header__left">
                <span className="ico--up ico--xs project-group-header__chevron" />
                <span
                  className="project-group-header__dot"
                  style={{ backgroundColor: 'var(--grey-ux-400)' }}
                />
                <span className="project-group-header__name">{t('untagged')}</span>
                <span className="project-group-header__count">{ungroupedPrompts.length}</span>
              </div>
            </div>
            {!ungroupedCollapsed && renderDataTable(ungroupedPrompts, 'table-prompt-ungrouped', true)}
          </div>
        )}
      </div>
    );
  };

  const titleNode = tableTitleSubject ? (
    <span className="prompts-table__title">
      {tableTitle} <span className="prompts-table__title-subject">{tableTitleSubject}</span>
    </span>
  ) : (
    tableTitle
  );

  return (
    <>
      <ConfirmDialog className="dialog-box" />

      {stickyHeader && isMobile ? (
        <>
          <MobilePageHeader title={<span>{titleNode}</span>} actions={renderMobileHeaderActions()} />
          {selectionMode && selectedPrompts.length > 0 && (
            <div className="page-box__buttons-panel prompts-table__mobile-bulk">{renderBulkActions()}</div>
          )}
        </>
      ) : stickyHeader ? (
        <div className="page-box__filters" data-observe-sticky>
          <Title title={titleNode} children={renderButton()} />
        </div>
      ) : (
        <Title title={titleNode} children={renderButton()} />
      )}

      {isAccordionMode ? (
        renderAccordionContent()
      ) : showGroups ? (
        renderGroupedContent()
      ) : (
        <div className={`table-content${scrollHeight ? ' table-content--sticky-head' : ''} ${additionalClass ? additionalClass : ''}`}>
          {renderDataTable(data, 'table-prompts-flat')}
        </div>
      )}

      {projectId && (
        <ImportPromptsDialog
          visible={importDialogVisible}
          onHide={() => setImportDialogVisible(false)}
          projectId={projectId}
          availableSlots={Math.min(
            Math.max(
              0,
              (accountResponse?.maxMonitorCount ?? 0) -
                // No allocation: the pool left next to every other reservation (the plain usage without the hub).
                ((allocationLeft === null
                  ? accountResponse?.allocatedMonitorCount
                  : accountResponse?.createdMonitorCount) ?? 0)
            ),
            allocationLeft ?? Number.MAX_SAFE_INTEGER
          )}
          onSuccess={() => {
            setImportDialogVisible(false);
            refreshAfterDelete();
          }}
        />
      )}
    </>
  );
};

export default PromptsTable;
